import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, Upload, Camera, MapPin, Truck, Package, CheckCircle } from 'lucide-react';
import { FileUpload } from '@/components/files/FileUpload';
import { useDeliveryProofs } from '@/hooks/useDeliveryProofs';
import { useToast } from '@/hooks/use-toast';
import {
  DELIVERY_METHODS,
  DELIVERY_CONDITIONS,
  DELIVERY_PHOTO_TYPES,
  MIN_DELIVERY_PHOTOS,
  SIGNATURE_REQUIRED_METHODS,
  DeliveryMethod,
  DeliveryCondition,
  DeliveryPhotoType
} from '@/lib/types/delivery';

interface DeliverySubmissionFormProps {
  jobId: string;
  purchaseOrderId?: string;
  onSubmissionComplete: () => void;
}

export const DeliverySubmissionForm = ({ jobId, purchaseOrderId, onSubmissionComplete }: DeliverySubmissionFormProps) => {
  const { submitDeliveryProof } = useDeliveryProofs();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [deliveryPhotos, setDeliveryPhotos] = useState<any[]>([]);
  const [receipts, setReceipts] = useState<string[]>([]);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      delivery_method: '' as DeliveryMethod,
      condition_on_delivery: 'good' as DeliveryCondition,
      partial_delivery: false,
      warranty_activation: false,
      training_provided: false,
      delivery_time: '',
      delivery_name: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state_province: '',
      postal_code: '',
      contact_name: '',
      contact_phone: '',
      carrier_name: '',
      tracking_number: '',
      driver_name: '',
      driver_contact: '',
      packing_slip_url: '',
      invoice_reference: '',
      installation_notes: '',
      delivery_notes: '',
      exceptions_noted: '',
    }
  });

  const deliveryMethod = watch('delivery_method');
  const partialDelivery = watch('partial_delivery');
  const requiresSignature = SIGNATURE_REQUIRED_METHODS.includes(deliveryMethod);

  const onSubmit = async (data: any) => {
    try {
      setSubmitting(true);

      // Validate required photos
      if (deliveryPhotos.length < MIN_DELIVERY_PHOTOS) {
        toast({
          title: "Insufficient Documentation",
          description: `At least ${MIN_DELIVERY_PHOTOS} photos are required for delivery proof`,
          variant: "destructive",
        });
        return;
      }

      // Validate required photo types
      const photoTypes = deliveryPhotos.map(p => p.photo_type);
      const hasBeforeDelivery = photoTypes.includes('before_delivery');
      const hasAfterDelivery = photoTypes.includes('after_delivery');
      
      if (!hasBeforeDelivery || !hasAfterDelivery) {
        toast({
          title: "Missing Required Photos",
          description: "Both 'before delivery' and 'after delivery' photos are required",
          variant: "destructive",
        });
        return;
      }

      // Validate delivery date
      if (!selectedDate) {
        toast({
          title: "Delivery Date Required",
          description: "Please select the delivery date",
          variant: "destructive",
        });
        return;
      }

      const deliveryData = {
        job_id: jobId,
        purchase_order_id: purchaseOrderId,
        delivery_date: selectedDate.toISOString(),
        delivery_time: data.delivery_time,
        delivery_method: deliveryMethod,
        delivery_location: {
          delivery_name: data.delivery_name,
          address_line1: data.address_line1,
          address_line2: data.address_line2,
          city: data.city,
          state_province: data.state_province,
          postal_code: data.postal_code,
          country: data.country || 'US',
          contact_name: data.contact_name,
          contact_phone: data.contact_phone,
          contact_email: data.contact_email,
          special_instructions: data.special_instructions,
        },
        carrier_info: {
          carrier_name: data.carrier_name,
          tracking_number: data.tracking_number,
          driver_name: data.driver_name,
          driver_contact: data.driver_contact,
        },
        items_delivered: data.items_delivered || [],
        partial_delivery: partialDelivery,
        condition_on_delivery: data.condition_on_delivery,
        delivery_notes: data.delivery_notes,
        exceptions_noted: data.exceptions_noted,
        delivery_photos: deliveryPhotos,
        delivery_receipts: receipts,
        packing_slip_url: data.packing_slip_url,
        invoice_reference: data.invoice_reference,
        recipient_signature: data.recipient_signature,
        installation_notes: data.installation_notes,
        warranty_activation: data.warranty_activation,
        training_provided: data.training_provided,
      };

      await submitDeliveryProof(deliveryData);

      toast({
        title: "Delivery Proof Submitted",
        description: "Your delivery documentation has been submitted for client review",
      });

      onSubmissionComplete();
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: error instanceof Error ? error.message : 'Failed to submit delivery proof',
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Delivery Basics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Delivery Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="delivery_date">Delivery Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label htmlFor="delivery_time">Delivery Time</Label>
              <Input
                {...register('delivery_time')}
                type="time"
                placeholder="HH:MM"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="delivery_method">Delivery Method *</Label>
            <Select
              value={deliveryMethod}
              onValueChange={(value) => setValue('delivery_method', value as DeliveryMethod)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select delivery method" />
              </SelectTrigger>
              <SelectContent>
                {DELIVERY_METHODS.map((method) => (
                  <SelectItem key={method.value} value={method.value}>
                    {method.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="condition_on_delivery">Condition on Delivery *</Label>
            <Select
              value={watch('condition_on_delivery')}
              onValueChange={(value) => setValue('condition_on_delivery', value as DeliveryCondition)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DELIVERY_CONDITIONS.map((condition) => (
                  <SelectItem key={condition.value} value={condition.value}>
                    {condition.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="partial_delivery"
              checked={partialDelivery}
              onCheckedChange={(checked) => setValue('partial_delivery', !!checked)}
            />
            <Label htmlFor="partial_delivery">This is a partial delivery</Label>
          </div>
        </CardContent>
      </Card>

      {/* Delivery Location */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Delivery Location
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="delivery_name">Location Name *</Label>
            <Input
              {...register('delivery_name', { required: 'Location name is required' })}
              placeholder="e.g., Main Office, Warehouse A"
            />
            {errors.delivery_name && (
              <p className="text-sm text-destructive mt-1">{errors.delivery_name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="address_line1">Address Line 1 *</Label>
            <Input
              {...register('address_line1', { required: 'Address is required' })}
              placeholder="Street address"
            />
            {errors.address_line1 && (
              <p className="text-sm text-destructive mt-1">{errors.address_line1.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="address_line2">Address Line 2</Label>
            <Input
              {...register('address_line2')}
              placeholder="Apartment, suite, unit, building, floor, etc."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="city">City *</Label>
              <Input
                {...register('city', { required: 'City is required' })}
                placeholder="City"
              />
            </div>

            <div>
              <Label htmlFor="state_province">State/Province *</Label>
              <Input
                {...register('state_province', { required: 'State/Province is required' })}
                placeholder="State or Province"
              />
            </div>

            <div>
              <Label htmlFor="postal_code">Postal Code *</Label>
              <Input
                {...register('postal_code', { required: 'Postal code is required' })}
                placeholder="ZIP/Postal Code"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact_name">Contact Name</Label>
              <Input
                {...register('contact_name')}
                placeholder="Person who received delivery"
              />
            </div>

            <div>
              <Label htmlFor="contact_phone">Contact Phone</Label>
              <Input
                {...register('contact_phone')}
                placeholder="Phone number"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Carrier Information */}
      {(deliveryMethod === 'shipping' || deliveryMethod === 'pickup') && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Carrier & Tracking
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="carrier_name">Carrier Name</Label>
                <Input
                  {...register('carrier_name')}
                  placeholder="UPS, FedEx, USPS, etc."
                />
              </div>

              <div>
                <Label htmlFor="tracking_number">Tracking Number</Label>
                <Input
                  {...register('tracking_number')}
                  placeholder="Tracking/reference number"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="driver_name">Driver Name</Label>
                <Input
                  {...register('driver_name')}
                  placeholder="Delivery driver name"
                />
              </div>

              <div>
                <Label htmlFor="driver_contact">Driver Contact</Label>
                <Input
                  {...register('driver_contact')}
                  placeholder="Driver phone/contact"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Photo Documentation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Photo Documentation
            <Badge variant={deliveryPhotos.length >= MIN_DELIVERY_PHOTOS ? "default" : "destructive"}>
              {deliveryPhotos.length}/{MIN_DELIVERY_PHOTOS} minimum
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Please provide photos documenting the delivery. Required: before delivery and after delivery photos.
          </div>
          
          <FileUpload
            entityType="completion"
            entityId={jobId}
            onUploadComplete={() => {
              // Photo handling would be implemented here
            }}
            multiple
            className="w-full"
          />

          {deliveryPhotos.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Uploaded Photos:</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {deliveryPhotos.map((photo, index) => (
                  <div key={index} className="p-2 border rounded text-sm">
                    <Badge variant="outline" className="text-xs">
                      {photo.photo_type.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Additional Documentation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Additional Documentation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="packing_slip_url">Packing Slip URL</Label>
            <Input
              {...register('packing_slip_url')}
              placeholder="Link to packing slip document"
            />
          </div>

          <div>
            <Label htmlFor="invoice_reference">Invoice Reference</Label>
            <Input
              {...register('invoice_reference')}
              placeholder="Invoice number or reference"
            />
          </div>

          <FileUpload
            entityType="completion"
            entityId={jobId}
            onUploadComplete={() => {
              // Receipt handling would be implemented here
            }}
            multiple
            className="w-full"
          />
        </CardContent>
      </Card>

      {/* Installation Specific */}
      {deliveryMethod === 'installation' && (
        <Card>
          <CardHeader>
            <CardTitle>Installation Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="installation_notes">Installation Notes</Label>
              <Textarea
                {...register('installation_notes')}
                placeholder="Details about the installation process, any issues encountered, etc."
                rows={4}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="warranty_activation"
                checked={watch('warranty_activation')}
                onCheckedChange={(checked) => setValue('warranty_activation', !!checked)}
              />
              <Label htmlFor="warranty_activation">Warranty activated</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="training_provided"
                checked={watch('training_provided')}
                onCheckedChange={(checked) => setValue('training_provided', !!checked)}
              />
              <Label htmlFor="training_provided">Training provided to client</Label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delivery Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Delivery Notes & Exceptions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="delivery_notes">Delivery Notes</Label>
            <Textarea
              {...register('delivery_notes')}
              placeholder="Any general notes about the delivery..."
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="exceptions_noted">Exceptions or Issues</Label>
            <Textarea
              {...register('exceptions_noted')}
              placeholder="Document any exceptions, damages, or issues encountered during delivery..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Submit Button */}
      <div className="flex justify-end gap-4">
        <Button
          type="submit"
          disabled={submitting || deliveryPhotos.length < MIN_DELIVERY_PHOTOS}
          size="lg"
          className="min-w-[200px]"
        >
          {submitting ? 'Submitting...' : 'Submit Delivery Proof'}
        </Button>
      </div>
    </form>
  );
};